import { morph } from '@theme/morph';
import { StandardEvents } from '@shopify/events';

class HzPurchaseOptions extends HTMLElement {
  #section;
  #reassurance;
  #reassuranceTimer;
  #reassuranceTransitionTimer;

  connectedCallback() {
    this.#section = this.closest('.shopify-section, dialog');
    if (!this.#section) return;

    this.#section.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    this.addEventListener('change', this.#handlePurchaseChange);
    this.#syncButtonPrice();
    this.#setupReassurance();
  }

  disconnectedCallback() {
    this.#section?.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    this.removeEventListener('change', this.#handlePurchaseChange);
    this.#clearReassuranceTimers();
    this.#section = undefined;
  }

  #handlePurchaseChange = (event) => {
    if (!(event.target instanceof HTMLInputElement) || event.target.name !== 'selling_plan') return;
    this.#syncButtonPrice();
  };

  #syncButtonPrice = () => {
    const selectedInput = this.querySelector('input[name="selling_plan"]:checked');
    const price = selectedInput?.dataset.price;
    const button = this.closest('product-form-component')?.querySelector('[ref="addToCartButton"]');
    const label = button?.querySelector('[data-add-to-cart-label]');

    if (!price || !label) return;

    if (!label.dataset.baseLabel) {
      label.dataset.baseLabel = label.textContent?.trim().replace(/\s+—\s+.*$/, '') || '';
    }

    label.textContent = `${label.dataset.baseLabel} — ${price}`;
  };

  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;
    if (!event.promise || typeof event.promise.then !== 'function') return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const sourceProductId = event.target.closest('variant-picker')?.dataset.productId;
        const currentProductId = this.closest('product-form-component')?.dataset.productId;
        const eventProductIds = [detail.productId, detail.newProduct?.id].filter(Boolean);
        if (sourceProductId && detail.productId && sourceProductId !== detail.productId && !detail.newProduct) return;
        if (currentProductId && eventProductIds.length > 0 && !eventProductIds.includes(currentProductId)) return;

        const nextOptions = detail.html.querySelector('[data-hz-purchase-options]');
        if (!nextOptions) {
          this.#removeReassurance();
          this.querySelectorAll('input[name="selling_plan"]').forEach((input) => {
            input.checked = false;
            input.disabled = true;
          });
          if (detail.resource?.id) {
            this.setAttribute('data-variant-id', detail.resource.id);
          } else {
            this.removeAttribute('data-variant-id');
          }
          if (typeof detail.resource?.requires_selling_plan === 'boolean') {
            this.setAttribute('data-requires-selling-plan', detail.resource.requires_selling_plan);
          } else {
            this.removeAttribute('data-requires-selling-plan');
          }
          this.hidden = true;
          this.setAttribute('aria-hidden', 'true');
          return;
        }

        const nextIsHidden = nextOptions.hasAttribute('hidden');
        morph(this, nextOptions);
        for (const attribute of ['data-variant-id', 'data-requires-selling-plan']) {
          const value = nextOptions.getAttribute(attribute);
          if (value === null) {
            this.removeAttribute(attribute);
          } else {
            this.setAttribute(attribute, value);
          }
        }

        this.hidden = nextIsHidden;
        if (nextIsHidden) {
          this.setAttribute('aria-hidden', 'true');
        } else {
          this.removeAttribute('aria-hidden');
        }
        this.#syncButtonPrice();
        this.#setupReassurance();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[hz-purchase-options] Variant update failed:', error);
      });
  };

  #setupReassurance = () => {
    this.#clearReassuranceTimers();

    const nextReassurance = this.querySelector('[data-hz-purchase-reassurance]');
    if (nextReassurance) this.#reassurance = nextReassurance;
    if (!this.#reassurance) return;

    const productForm = this.closest('product-form-component');
    const addToCartButton = productForm?.querySelector('[ref="addToCartButton"]');
    const addToCartWrapper = addToCartButton?.closest('span') || addToCartButton;
    if (!productForm || !addToCartWrapper) return;

    productForm.querySelectorAll('[data-hz-purchase-reassurance]').forEach((element) => {
      if (element !== this.#reassurance) element.remove();
    });
    addToCartWrapper.after(this.#reassurance);

    const message = this.#reassurance.querySelector('.hz-purchase-options__reassurance-message');
    const messages = (this.#reassurance.dataset.messages || '')
      .split(/<br\s*\/?\s*>/i)
      .map((item) => item.trim())
      .filter(Boolean);
    const interval = Number(this.#reassurance.dataset.interval) || 4000;
    if (!message || messages.length < 2) return;

    let index = 0;
    message.textContent = messages[index];
    this.#reassuranceTimer = window.setInterval(() => {
      message.classList.add('is-changing');
      this.#reassuranceTransitionTimer = window.setTimeout(() => {
        index = (index + 1) % messages.length;
        message.textContent = messages[index];
        message.classList.remove('is-changing');
      }, 160);
    }, interval);
  };

  #clearReassuranceTimers = () => {
    window.clearInterval(this.#reassuranceTimer);
    window.clearTimeout(this.#reassuranceTransitionTimer);
    this.#reassuranceTimer = undefined;
    this.#reassuranceTransitionTimer = undefined;
  };

  #removeReassurance = () => {
    this.#clearReassuranceTimers();
    this.#reassurance?.remove();
    this.#reassurance = undefined;
  };

}

if (!customElements.get('hz-purchase-options')) {
  customElements.define('hz-purchase-options', HzPurchaseOptions);
}
